/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Module, Node, Waiting } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import {
    ICheckoutViewProps,
    ILineItem,
    ILineItemDeliveryGroup,
    ILineItems,
    IOrderSummary,
    IPickUpAtStore
} from '@msdyn365-commerce-modules/checkout/src/modules/checkout/./checkout';
import { IEmailDelivery } from '@msdyn365-commerce-modules/checkout/src/modules/checkout/./components';
import { IInvoicePaymentSummary } from '@msdyn365-commerce-modules/checkout/src/modules/checkout/./components/get-invoice-payment-summary';
import { ICheckoutProps } from '../definition-extensions/checkout.ext.props.autogenerated';
import { changeMailChimpSubscriptionStatusAsync } from '../../../actions/DataActionExtension.g';
import MsDyn365 from '@msdyn365-commerce/core';
import { Cart } from '@msdyn365-commerce/retail-proxy';
// from checkout.ext.data.tsx file, which is now deleted
import { IGetMailchimpSubscriptionStatusData } from '../../../actions/get-mailchimp-subscription-status.action';
import { AsyncResult } from '@msdyn365-commerce/retail-proxy';
import get from 'lodash/get';
import { CheckoutProvider } from '../../../utilities/CheckoutContext';
import { getTelemetryObject } from '@msdyn365-commerce-modules/utilities';
export interface IExtendedOrderSummary extends IOrderSummary {
    cart: Cart | undefined;
}
import { PromoCodeComponent } from '@msdyn365-commerce/components';
// from checkout.ext.data.tsx file, which is now deleted
export interface ICheckoutExtData {
    mailchimpSubscriptionStatus: AsyncResult<IGetMailchimpSubscriptionStatusData>;
}

export const PickUpAtStoreComponent: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <Node {...PickUpAtStore}>
        {label}
        {location}
    </Node>
);

export const EmailDeliveryComponent: React.FC<IEmailDelivery> = ({ EmailDelivery, label }) => <Node {...EmailDelivery}>{label}</Node>;

export const LineItemComponent: React.FC<ILineItem> = ({ LineItem, item, pickUpAtStore, emailDelivery }) => (
    <Node {...LineItem}>
        {item}
        {pickUpAtStore && <PickUpAtStoreComponent {...pickUpAtStore} />}
        {emailDelivery && <EmailDeliveryComponent {...emailDelivery} />}
    </Node>
);

export const LineItemGroupComponent: React.FC<ILineItemDeliveryGroup> = ({ LineItemDeliveryGroup, LineItemList, heading, lineItems }) => (
    <Node {...LineItemDeliveryGroup}>
        {heading}
        <Node {...LineItemList}>
            {lineItems.map(lineItem => (
                <LineItemComponent key={lineItem.LineId} {...lineItem} />
            ))}
        </Node>
    </Node>
);

export const LineItemGroupComponentWithMultiplePickUp: React.FC<ILineItemDeliveryGroup> = ({
    LineItemDeliveryGroup,
    LineItemList,
    heading,
    lineItems,
    lineItemWraper,
    lineItemWraperIcon
}) => (
    <Node {...LineItemDeliveryGroup}>
        {lineItemWraperIcon}
        {lineItemWraper}
        {heading}
        <Node {...LineItemList}>
            {lineItems.map(lineItem => (
                <LineItemComponentWithMultiplePickUp key={lineItem.LineId} {...lineItem} />
            ))}
        </Node>
    </Node>
);

export const LineItemComponentWithMultiplePickUp: React.FC<ILineItem> = ({ LineItem, item, pickUpAtStore, emailDelivery }) => (
    <Node {...LineItem}>
        {item}
        {emailDelivery && <EmailDeliveryComponent {...emailDelivery} />}
    </Node>
);

export const PickUpAtStoreComponentWithMultiplePickUp: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <Node {...PickUpAtStore}>
        {label}
        {location}
    </Node>
);

export const LineItemsComponent: React.FC<ILineItems> = ({
    LineItems,
    Header,
    heading,
    editLink,
    itemsForPickup,
    itemsForShip,
    itemsForEmail,
    itemsGroupWithMulitplePickupMode
}) => (
    <Node {...LineItems}>
        <Node {...Header}>
            {heading}
            {editLink}
        </Node>
        {itemsGroupWithMulitplePickupMode === undefined && itemsForPickup && <LineItemGroupComponent {...itemsForPickup} />}
        {itemsGroupWithMulitplePickupMode === undefined && itemsForEmail && <LineItemGroupComponent {...itemsForEmail} />}
        {itemsGroupWithMulitplePickupMode === undefined && itemsForShip && <LineItemGroupComponent {...itemsForShip} />}
        {itemsGroupWithMulitplePickupMode !== undefined
            ? itemsGroupWithMulitplePickupMode.map((item, index) => {
                  return <LineItemGroupComponentWithMultiplePickUp {...item} key={index} />;
              })
            : null}
    </Node>
);

const OrderSummaryComponent: React.FC<IExtendedOrderSummary> = ({ heading, lines, cart }) => (
    <>
        {heading}
        <div className='msc-order-summary-wrapper'>
            <div className='msc-order-summary__items'>
                {lines && (
                    <>
                        {React.cloneElement(lines.subtotal as React.ReactElement, { price: cart?.SubtotalAmountWithoutTax })}
                        {lines.shipping}
                        {lines.otherCharge}
                        {lines.tax}
                        {lines.loyalty}
                        {lines.giftCard}
                        {lines.orderTotal}
                    </>
                )}
            </div>
        </div>
    </>
);

const PaymentSummaryComponent: React.FC<IInvoicePaymentSummary> = ({ heading, lines }) => (
    <>
        {heading}
        <div className='msc-invoice-summary-wrapper'>
            <div className='msc-invoice-summary__items'>
                {lines && (
                    <>
                        {lines.invoices}
                        {lines.giftCard}
                        {lines.loyalty}
                        {lines.orderTotal}
                    </>
                )}
            </div>
        </div>
    </>
);

const CheckoutView: React.FC<ICheckoutViewProps & ICheckoutProps<ICheckoutExtData>> = props => {
    const {
        isPaymentVerificationRedirection,
        shouldEnableSinglePaymentAuthorizationCheckout,
        canShow,
        checkoutProps,
        headerProps,
        hasSalesOrder,
        hasInvoiceLine,
        bodyProps,
        mainProps,
        mainControlProps,
        sideProps,
        sideControlFirstProps,
        sideControlSecondProps,
        termsAndConditionsProps,
        orderConfirmation,
        loading,
        alert,
        title,
        guidedForm,
        orderSummary,
        invoicePaymentSummary,
        lineItems,
        placeOrderButton,
        termsAndConditions,
        // keepShoppingButton,
        data: { mailchimpSubscriptionStatus },
        resources: { subscribeCheckboxLabel },
        resources
    } = props;
    const isMailchimpChecked = mailchimpSubscriptionStatus.result && mailchimpSubscriptionStatus.result.newsletterActive;
    const isSignedInUser = props.context.actionContext.requestContext.user.isAuthenticated;
    const shouldShowMailchimp = isMailchimpChecked === false && isSignedInUser;
    let [isErr, setIsErr] = React.useState<boolean>(false);

    const renderSubscriptionCheckbox = () => {
        const setChecked = (event: React.ChangeEvent<HTMLInputElement>) => {
            const checkbox = event.target;
            changeMailChimpSubscriptionStatusAsync({ callerContext: props.context.actionContext }, checkbox.checked, false).then(res => {
                if (!res.Success) {
                    !checkbox.checked;
                }
            });
        };
        return (
            <div className={'checkout-subscribe'}>
                <input type='checkbox' onChange={setChecked} id={'subscriptionCheckbox'} className={'checkout-subscribe__checkbox'} />
                <label htmlFor='subscriptionCheckbox' className={'checkout-subscribe__label'}>
                    {subscribeCheckboxLabel}
                </label>
            </div>
        );
    };

    const checkErr = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        const classes = target.classList;
        if (classes.contains('ms-checkout__btn-place-order') && classes.contains('disabled') && !classes.contains('is-busy')) {
            setIsErr(true);
            setTimeout(function() {
                setIsErr(false);
            }, 6000);
        }
    };
    if (MsDyn365.isBrowser) {
        let orderbtn = document.querySelectorAll<HTMLButtonElement>('button.ms-checkout__btn-place-order');
        orderbtn.forEach(btn => {
            btn.disabled = false;
        });

        document.addEventListener('click', checkErr);
    }

    const _renderPlaceOrderBtn = () => {
        if (!placeOrderButton) {
            return null;
        }
        const isTermsChecked = props.data.checkout?.result?.isTermsAndConditionChecked;
        return isTermsChecked ? placeOrderButton : React.cloneElement(placeOrderButton as React.ReactElement, { canPlaceOrder: false });
    };

    const cartHasNoBalance = () => {
        const orderTotalNode = orderSummary?.lines?.orderTotal;
        if (orderTotalNode) {
            const amountDue = get(orderTotalNode, 'props.price') || 0;

            return (amountDue as number) === 0;
        }
        return false;
    };
    cartHasNoBalance();
    return (
        <CheckoutProvider
            value={{
                placeOrderButton: _renderPlaceOrderBtn(),
                cartHasBalance: !cartHasNoBalance(),
                mailChimp: renderSubscriptionCheckbox(),
                termsAndConditions: termsAndConditions
            }}
        >
            <Module {...checkoutProps}>
                {!hasSalesOrder && <Node {...headerProps}>{title}</Node>}
                {!hasSalesOrder &&
                    isPaymentVerificationRedirection &&
                    shouldEnableSinglePaymentAuthorizationCheckout &&
                    !alert &&
                    !loading && <Waiting className='msc-waiting-circular msc-waiting-lg' />}
                {!hasSalesOrder && (
                    <Node {...bodyProps}>
                        {loading}
                        {alert}
                        {canShow && (
                            <>
                                <Node {...mainProps}>
                                    {guidedForm}
                                    <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                                    {/*{shouldShowMailchimp && renderSubscriptionCheckbox()}*/}
                                    <Node {...mainControlProps}>
                                        {_renderPlaceOrderBtn()}
                                        {/*{keepShoppingButton}*/}
                                    </Node>
                                </Node>
                                <Node {...sideProps}>
                                    {!hasInvoiceLine
                                        ? orderSummary && (
                                              <OrderSummaryComponent
                                                  {...orderSummary}
                                                  cart={props.data.checkout.result?.checkoutCart.cart}
                                              />
                                          )
                                        : invoicePaymentSummary && <PaymentSummaryComponent {...invoicePaymentSummary} />}
                                    <Node {...sideControlFirstProps}>
                                        {<Node {...termsAndConditionsProps}>{termsAndConditions}</Node>}
                                        {shouldShowMailchimp && renderSubscriptionCheckbox()}
                                        <div className={isErr ? 'ordererr show' : 'ordererr'}>
                                            {props.resources.checkoutNotCompleteErrText}
                                        </div>

                                        {cartHasNoBalance() && _renderPlaceOrderBtn()}
                                        {/*{keepShoppingButton}*/}
                                    </Node>
                                    {lineItems && <LineItemsComponent {...lineItems} />}
                                    <PromoCodeComponent
                                        cart={props.data.checkout.result?.checkoutCart}
                                        promoCodeHeadingText={resources.promoCodeHeadingText}
                                        promoPlaceholderText={resources.promoPlaceholderText}
                                        promoCodeApplyButtonText={resources.promoCodeApplyButtonText}
                                        appliedPromoCodeHeadingText={resources.appliedPromoCodeHeadingText}
                                        collapseTimeOut={350}
                                        removePromoAriaLabelFormat={resources.removePromoAriaLabelFormat}
                                        removePromoText={resources.removePromoText}
                                        invalidPromoCodeErrorText={resources.invalidPromoCodeErrorText}
                                        failedToAddPromoCodeErrorText={resources.failedToAddPromoCodeErrorText}
                                        failedToRemovePromoCodeErrorText={resources.failedToRemovePromoCodeErrorText}
                                        duplicatePromoCodeErrorText={resources.duplicatePromotionErrorText}
                                        context={props.context}
                                        id={props.id}
                                        typeName={props.typeName}
                                        data={{}}
                                        telemetryContent={getTelemetryObject(
                                            props.context.request.telemetryPageName!,
                                            props.friendlyName,
                                            props.telemetry
                                        )}
                                    />
                                    <Node {...sideControlSecondProps}>
                                        {<Node {...termsAndConditionsProps}>{termsAndConditions}</Node>}
                                        {/*{shouldShowMailchimp && renderSubscriptionCheckbox()}*/}
                                        <div className={isErr ? 'ordererr show' : 'ordererr'}>
                                            {props.resources.checkoutNotCompleteErrText}
                                        </div>
                                        {_renderPlaceOrderBtn()}
                                        {/*{keepShoppingButton}*/}
                                    </Node>
                                </Node>
                            </>
                        )}
                    </Node>
                )}
                {hasSalesOrder && orderConfirmation}
            </Module>
        </CheckoutProvider>
    );
};

export default CheckoutView;
